import { useState, useEffect, useCallback } from "react"
import { useAuth } from "react-oidc-context"
import { useRole } from "@/hooks/useRole"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  listIntegrations,
  initiateNetSuiteConnect,
  completeNetSuiteConnect,
  disconnectNetSuite,
  getIntegrationSettings,
  updateIntegrationSettings,
  type IntegrationStatus,
  type IntegrationSettings,
} from "@/services/integrationService"

const NETSUITE_ACCOUNT_ID_KEY = "netsuite-account-id"

interface NetSuiteSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function NetSuiteSettingsDialog({
  open,
  onOpenChange,
}: NetSuiteSettingsDialogProps) {
  const auth = useAuth()
  const { isAdmin } = useRole()
  const [accountId, setAccountId] = useState(
    () => localStorage.getItem(NETSUITE_ACCOUNT_ID_KEY) || ""
  )
  const [integration, setIntegration] = useState<IntegrationStatus | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")

  // Admin settings state
  const [settings, setSettings] = useState<IntegrationSettings | null>(null)
  const [clientIdInput, setClientIdInput] = useState("")
  const [isSavingSettings, setIsSavingSettings] = useState(false)
  const [settingsMessage, setSettingsMessage] = useState("")

  const idToken = auth.user?.id_token || ""

  const loadStatus = useCallback(async () => {
    if (!idToken) return
    setIsLoading(true)
    try {
      const integrations = await listIntegrations(idToken)
      const ns = integrations.find((i) => i.integrationId === "netsuite")
      setIntegration(ns || null)
      if (ns?.netsuiteAccountId) {
        localStorage.setItem(NETSUITE_ACCOUNT_ID_KEY, ns.netsuiteAccountId)
        setAccountId(ns.netsuiteAccountId)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "連携状態の取得に失敗しました")
    } finally {
      setIsLoading(false)
    }
  }, [idToken])

  const loadSettings = useCallback(async () => {
    if (!idToken || !isAdmin) return
    try {
      const s = await getIntegrationSettings(idToken)
      setSettings(s)
    } catch {
      // Admin settings load failure is non-critical
    }
  }, [idToken, isAdmin])

  useEffect(() => {
    if (open) {
      loadStatus()
      loadSettings()
      setError("")
      setSettingsMessage("")
    }
  }, [open, loadStatus, loadSettings])

  // Listen for OAuth callback messages from popup (only when dialog is open)
  // The popup sends code+state; this window performs the token exchange (has Cognito session)
  useEffect(() => {
    if (!open) return

    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== "netsuite-oauth-callback") return

      if (!event.data?.success) {
        setIsConnecting(false)
        setError(event.data?.error || "OAuth 認証に失敗しました")
        return
      }

      // Popup sent code and state — perform token exchange here
      const { code, state } = event.data
      if (code && state) {
        try {
          await completeNetSuiteConnect(idToken, code, state)
          setIsConnecting(false)
          await loadStatus()
        } catch (e) {
          setIsConnecting(false)
          setError(e instanceof Error ? e.message : "トークン交換に失敗しました")
        }
      } else {
        // Legacy: popup already completed exchange (shouldn't happen with new flow)
        setIsConnecting(false)
        await loadStatus()
      }
    }
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [open, idToken, loadStatus])

  const handleSaveSettings = async () => {
    if (!clientIdInput.trim()) return
    setIsSavingSettings(true)
    setSettingsMessage("")

    try {
      await updateIntegrationSettings(idToken, { netsuiteClientId: clientIdInput.trim() })
      setSettings({ netsuiteClientId: "", isConfigured: true })
      setClientIdInput("")
      setSettingsMessage("Client ID を保存しました")
    } catch (e) {
      setSettingsMessage(e instanceof Error ? e.message : "保存に失敗しました")
    } finally {
      setIsSavingSettings(false)
    }
  }

  const handleConnect = async () => {
    if (!accountId.trim()) {
      setError("NetSuite アカウント ID を入力してください")
      return
    }

    setIsConnecting(true)
    setError("")
    localStorage.setItem(NETSUITE_ACCOUNT_ID_KEY, accountId.trim())

    try {
      const { authorizationUrl } = await initiateNetSuiteConnect(idToken, accountId.trim())

      const width = 600
      const height = 700
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      window.open(
        authorizationUrl,
        "netsuite-oauth",
        `width=${width},height=${height},left=${left},top=${top},popup=1`
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : "接続の開始に失敗しました")
      setIsConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    setIsDisconnecting(true)
    setError("")

    try {
      await disconnectNetSuite(idToken)
      setIntegration(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : "切断に失敗しました")
    } finally {
      setIsDisconnecting(false)
    }
  }

  const isConnected = integration?.status === "connected"
  const isConfigured = settings?.isConfigured ?? true // default true for non-admin users

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>NetSuite 連携設定</DialogTitle>
          <DialogDescription>
            NetSuite アカウントを接続して、ERP データをレポートやスライドに活用できます。
          </DialogDescription>
        </DialogHeader>

        {/* Admin settings section */}
        {isAdmin && (
          <div className="space-y-3 rounded-md border p-3">
            <h4 className="text-sm font-medium">管理設定</h4>
            <div className="space-y-2">
              <Label htmlFor="clientId">NetSuite Client ID</Label>
              <div className="flex gap-2">
                <Input
                  id="clientId"
                  placeholder={settings?.netsuiteClientId || "Client ID を入力"}
                  value={clientIdInput}
                  onChange={(e) => setClientIdInput(e.target.value)}
                  disabled={isSavingSettings}
                />
                <Button
                  size="sm"
                  onClick={handleSaveSettings}
                  disabled={isSavingSettings || !clientIdInput.trim()}
                >
                  {isSavingSettings ? "保存中..." : "保存"}
                </Button>
              </div>
              {settings?.isConfigured && !clientIdInput && (
                <p className="text-xs text-muted-foreground">
                  設定済み: {settings.netsuiteClientId}
                </p>
              )}
              {settingsMessage && (
                <p className={`text-xs ${settingsMessage.includes("保存しました") ? "text-green-600" : "text-destructive"}`}>
                  {settingsMessage}
                </p>
              )}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">読み込み中...</div>
        ) : isConnected ? (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500" />
              <span className="text-sm font-medium">接続済み</span>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div>
                <span className="font-medium">アカウント ID:</span>{" "}
                {integration.netsuiteAccountId}
              </div>
              <div>
                <span className="font-medium">接続日時:</span>{" "}
                {new Date(integration.connectedAt).toLocaleString("ja-JP")}
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-gray-400" />
              <span className="text-sm text-muted-foreground">未接続</span>
            </div>
            {!isConfigured && !isAdmin ? (
              <p className="text-sm text-muted-foreground">
                管理者が NetSuite Client ID を設定するまで接続できません。
              </p>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="accountId">NetSuite アカウント ID</Label>
                <Input
                  id="accountId"
                  placeholder="例: 1234567 または TSTDRV1234567"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  disabled={isConnecting}
                />
                <p className="text-xs text-muted-foreground">
                  NetSuite の「設定 &gt; 会社 &gt; 会社情報」で確認できます。
                  <br />
                  ※ アカウント ID に「_」が含まれる場合は「-」に置き換えてください。
                </p>
              </div>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter>
          {isConnected ? (
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? "切断中..." : "切断"}
            </Button>
          ) : (
            <Button
              onClick={handleConnect}
              disabled={isConnecting || !accountId.trim() || (!isConfigured && !isAdmin)}
            >
              {isConnecting ? "接続中..." : "NetSuite に接続"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
